import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { LuExternalLink, LuMail } from "react-icons/lu";
import { useQuery } from "@/lib/convex";
import { Fragment, useMemo, type ReactNode } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import {
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  Heading,
  HStack,
  Icon,
  Separator,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  Wrap,
} from "@chakra-ui/react";
import type { FunctionReturnType } from "convex/server";
import { api } from "@convex/_generated/api";
import { CardGridSkeleton } from "../components/LoadingSkeletons";
import { TableSkeleton } from "../components/TableSkeleton";
import { PageHeader } from "../components/PageHeader";
import { DataTable } from "../components/DataTable";
import { FilterBar, type FilterNode } from "../components/FilterBar";
import { FilterCount } from "../components/FilterCount";
import { SearchInput } from "../components/SearchInput";
import { searchUpdate } from "@/lib/search";
import { fold } from "@/lib/highlightMatch";
import { RoleIcon, resolveMemberRole, roleTitle } from "../components/RoleIcon";
import { TextLink } from "../components/TextLink";
import { PROGRAMME_LABELS } from "@convex/schemaEnums";
import { groupLink } from "@/lib/groupLink";
import { Tooltip } from "../components/ui/tooltip";

// Derived from the queries, not hand-written. A local shape plus an `as` cast
// silences the compiler, which is how listGroups changing shape reached the
// browser instead of the build.
type GroupsOverview = FunctionReturnType<typeof api.persons.listGroups>;

// A narrowing, not a set of independent switches: everything, then the open
// units, then one kind of open unit. The type options mean OPEN units of that
// type, because "closed core groups" is not a question anyone asks -- the four
// closed labs are historical and are looked at as one group or not at all.
export const GROUP_FILTERS = ["all", "open", "core", "adjunct", "facility", "admin", "closed"] as const;
type GroupFilter = (typeof GROUP_FILTERS)[number];
type GroupRow = GroupsOverview["groups"][number];
type SubUnitRow = GroupRow["subUnits"][number];
// Sub-units render as child rows, so the table works on either shape.
type GroupTreeRow = GroupRow | SubUnitRow;

// Narrowed on the parent, not the child: a parent row has every field a child
// has plus more, so excluding the child type would exclude the parent with it
// and leave `never`.
function isParentUnit(row: GroupTreeRow): row is GroupRow {
  return "subUnits" in row;
}
type GroupProfile = NonNullable<FunctionReturnType<typeof api.persons.getGroupProfileBySlug>>;
type GroupMember = GroupProfile["members"][number];

// Open or closed, not active or inactive. A unit closes; its people keep
// finishing theses and manuscripts, and its output history stays with it.
function GroupStatusBadge({ isClosed }: { isClosed: boolean }) {
  return (
    <Badge
      colorPalette={isClosed ? "gray" : "green"}
      variant={isClosed ? "subtle" : "solid"}
    >
      {isClosed ? "Closed" : "Open"}
    </Badge>
  );
}

function TypeBadge({ label }: { label: string }) {
  return (
    <Badge variant="outline">{label}</Badge>
  );
}

const groupColumnHelper = createColumnHelper<GroupTreeRow>();

function GroupsTable({ groups }: { groups: GroupRow[] }) {
  const data = useMemo<GroupTreeRow[]>(() => groups, [groups]);
  const columns = useMemo(
    () => [
      groupColumnHelper.accessor("name", {
        header: "Group",
        cell: ({ row }) => {
          const unit = row.original;
          const child = !isParentUnit(unit);
          const parentSlug = child ? (row.getParentRow()?.original.slug ?? null) : null;
          return (
            // Sub-units are indented, and link to their parent's page narrowed
            // to them: they have no page of their own.
            <Stack gap={1} pl={child ? 6 : 0}>
              {child ? (
                <Link to="/groups/$shortName" {...groupLink({ slug: unit.slug, parentSlug })}>
                  <Text fontWeight="medium" color="fg.muted" _hover={{ textDecoration: "underline" }}>{unit.name}</Text>
                </Link>
              ) : (
                <Link to="/groups/$shortName" params={{ shortName: unit.slug }}>
                  <Text fontWeight="semibold" _hover={{ textDecoration: "underline" }}>{unit.name}</Text>
                </Link>
              )}
              <HStack gap={2}>
                {/* Sub-units carry no code of their own. The three Molecular
                    Discovery Platform facilities inherited "MDP" from the
                    tracked group list, which is the platform's code, not
                    theirs -- showing it on each child just repeats the parent. */}
                {!child && unit.shortName ? (
                  <Text fontSize="xs" textTransform="uppercase" letterSpacing="0.18em" color="fg.muted">{unit.shortName}</Text>
                ) : null}
                {child ? (
                  <Badge variant="subtle" colorPalette="gray">sub-group</Badge>
                ) : null}
              </HStack>
            </Stack>
          );
        },
      }),
      groupColumnHelper.accessor("typeLabel", {
        header: "Type",
        cell: ({ getValue }) => <TypeBadge label={getValue() ?? "Unknown"} />,
      }),
      groupColumnHelper.accessor("isClosed", {
        header: "Status",
        cell: ({ getValue }) => <GroupStatusBadge isClosed={getValue()} />,
      }),
      groupColumnHelper.accessor((row) => row.headNames[0] ?? "", {
        id: "heads",
        header: "Heads",
        cell: ({ row }) => <Text fontSize="sm" color="fg.muted">{row.original.headNames.length > 0 ? row.original.headNames.join(", ") : "—"}</Text>,
      }),
      // An accessor, not a display column: TanStack cannot sort a display column
      // because it has no value to compare. The accessor returns the member
      // count so the header sorts numerically, while the cell still renders the
      // breakdown beneath it.
      groupColumnHelper.accessor((row) => row.currentMemberCount, {
        id: "members",
        header: "Members",
        sortingFn: "basic",
        cell: ({ row }) => {
          const unit = row.original;
          const inSubUnits = isParentUnit(unit) ? unit.currentMemberCount - unit.ownMemberCount : 0;
          return (
            <Stack gap={0.5} fontSize="sm" color="fg.muted">
              {/* A parent's figure includes its sub-units, which is what it is
                  responsible for. Per-unit counts over-count people with several
                  affiliations by design; the deduplicated figure is on CeMMies. */}
              <Text>{unit.currentMemberCount} current</Text>
              {inSubUnits > 0 ? (
                <Text fontSize="xs">{inSubUnits} in sub-groups</Text>
              ) : null}
              {unit.cemmieCount === unit.currentMemberCount ? null : (
                <Text fontSize="xs">{unit.cemmieCount} count as CeMMies</Text>
              )}
            </Stack>
          );
        },
      }),
      groupColumnHelper.display({
        id: "website",
        header: "Website",
        cell: ({ row }) =>
          row.original.url ? (
            <Button asChild variant="ghost" size="sm" colorPalette="blue">
              <a href={row.original.url} target="_blank" rel="noreferrer">
                <LuExternalLink />
                Open
              </a>
            </Button>
          ) : (
            <Text fontSize="sm" color="fg.muted">—</Text>
          ),
      }),
    ],
    [],
  );

  return (
    <Box borderWidth="1px" borderColor="border">
      <DataTable
        data={data}
        columns={columns}
        getRowId={getGroupRowId}
        enableSorting
        stickyHeader
        getSubRows={(row) => (isParentUnit(row) ? row.subUnits : undefined)}
      />
    </Box>
  );
}

// Defined once, outside the component. An inline arrow is a new
// function on every render, and a table told its row identity has
// changed cannot skip anything.
const getGroupRowId = (row: { _id: string }) => row._id;
const getMemberRowId = (row: { affiliationId: string }) => row.affiliationId;

export function GroupsPage() {
  const overview = useQuery(api.persons.listGroups, {});
  // Open by default: the list answers "where does the institute work", and the
  // closed labs are history. Held in the address (?filter=closed), so the
  // default is the one value never written there.
  const params = useSearch({ from: "/_authed/groups" });
  const filter: GroupFilter = params.filter ?? "open";
  const search = params.q ?? "";
  const navigate = useNavigate({ from: "/groups" });
  const setFilter = (next: GroupFilter) =>
    void navigate({ search: (prev) => ({ ...prev, filter: next === "open" ? undefined : next }), ...searchUpdate });
  const setSearch = (next: string) =>
    void navigate({ search: (prev) => ({ ...prev, q: next === "" ? undefined : next }), ...searchUpdate });

  // By name, code or head. A group that matches keeps all its sub-groups; one
  // that matches only through a sub-group shows just those beneath it, so the
  // match is seen where it sits. Narrows the chips' counts too, as on CeMMies.
  const needle = fold(search.trim());
  const unitMatches = (unit: GroupTreeRow) =>
    fold([unit.name, unit.shortName ?? "", ...unit.headNames].join("\n")).includes(needle);
  const allGroups = overview?.groups ?? [];
  const groups = needle === ""
    ? allGroups
    : allGroups.flatMap((group) => {
      if (unitMatches(group)) {
        return [group];
      }
      const subUnits = group.subUnits.filter(unitMatches);
      return subUnits.length > 0 ? [{ ...group, subUnits }] : [];
    });
  const matches = (group: (typeof groups)[number], key: GroupFilter) => {
    if (key === "all") return true;
    if (key === "closed") return group.isClosed;
    if (key === "open") return !group.isClosed;
    return !group.isClosed && group.type === key;
  };

  const visibleGroups = groups.filter((group) => matches(group, filter));
  const countFor = (key: GroupFilter) => groups.filter((group) => matches(group, key)).length;

  // Open and closed partition the units; the kinds of unit narrow what is
  // already open, since "closed core groups" is not a question anyone asks --
  // the four closed labs are looked at as one group or not at all. Everything
  // is the state before any of these, so it has no chip.
  const filterOptions: FilterNode<GroupFilter>[] = [
    {
      key: "open",
      label: "Open",
      count: countFor("open"),
      description: "Groups the institute currently works in",
      children: [
        { key: "core", label: "Core", count: countFor("core"), description: "CeMM's own research groups" },
        { key: "adjunct", label: "Adjunct", count: countFor("adjunct"), description: "Affiliated groups whose people are mostly employed elsewhere" },
        { key: "facility", label: "Facilities", count: countFor("facility"), description: "Technology and service facilities" },
        { key: "admin", label: "Administration", count: countFor("admin"), description: "Administration and its sub-groups" },
      ],
    },
    {
      key: "closed",
      label: "Closed",
      count: countFor("closed"),
      description: "No longer active. Some members may still be finishing their work.",
    },
  ];


  return (
    <Box minH="full" bg="bg.subtle">
      <PageHeader
        title="Groups"
        description="Institute research groups, facilities and administration, with their heads and membership."
        breadcrumbs={[{ label: "CeMMbase", to: "/" }, { label: "Groups" }]}
      />

      <Stack gap={8} px={{ base: 6, sm: 8, lg: 10 }} py={{ base: 6, lg: 8 }}>
        {/* The filters at once, as on CeMMies; only what depends on the
            rows waits for them. */}
        <Stack gap={4}>
          <SearchInput
            placeholder="Search by group, code or head"
            value={search}
            onChange={setSearch}
          />
          <FilterBar
            options={filterOptions}
            value={filter}
            clearKey="all"
            onChange={setFilter}
            loading={overview === undefined}
          />
        </Stack>

        {overview === undefined ? (
          <Stack gap={2} align="stretch">
            <Box minH={8} display="flex" alignItems="center"><Skeleton height="4" width="10rem" /></Box>
            <TableSkeleton label="Loading groups" rows={10} columns={6} />
          </Stack>
        ) : (
          <>
            {/* The count belongs with the table, not with the filters: it
                describes the rows, and 32px of section spacing between the two
                left it reading as a footnote to the chips. */}
            <Stack gap={2} align="stretch">
              <FilterCount
                shown={visibleGroups.length}
                total={allGroups.length}
                noun={["group", "groups"]}
                filtered={filter !== "all" || needle !== ""}
                onClear={() => void navigate({ search: { filter: "all" }, ...searchUpdate })}
              />
              {visibleGroups.length === 0 ? (
                <Text fontSize="sm" color="fg.muted">
                  {needle !== "" && groups.length > 0
                    ? "No group here matches the search. The counts above show where one does."
                    : "No group matches. Try another search or filter."}
                </Text>
              ) : (
                <GroupsTable groups={visibleGroups} />
              )}
            </Stack>
          </>
        )}
      </Stack>
    </Box>
  );
}

/**
 * The person who runs a unit, or acts for them.
 *
 * Above the member table rather than pinned to the top of it: floating heads out
 * of alphabetical order makes them harder to find among their colleagues, and
 * "who runs this" is a different question from "who works here".
 */
const listFormat = new Intl.ListFormat("en", { style: "long", type: "conjunction" });

/**
 * One current member, as a card: the same card for a head and for everybody
 * else.
 *
 * Above the name, their role here and nothing else -- Head (of which
 * sub-group, where that is the case), Deputy head, PhD student, or Member --
 * with the icon the CeMMies list uses. Below it, their job title, then what
 * else is worth knowing: the sub-group they are in, their email, a PhD
 * cohort, an elected representative role, the group that is their primary
 * one where this is not, and a basis outside the headcount.
 */
function MemberCard({ member }: { member: GroupMember }) {
  const role = resolveMemberRole({ headRole: member.headRole, isStudent: member.isStudent });
  const roleText = member.headRole !== null && member.isSubUnit && member.ledSubUnitNames.length > 0
    ? `${roleTitle(role)} of ${listFormat.format(member.ledSubUnitNames)}`
    : roleTitle(role);
  // Which sub-unit they are in, when viewing a parent. More than one where a
  // person runs several -- the head of Proteomics and Metabolomics is one
  // member of the platform, listed once. A head of one already says it above.
  const subGroup = (member.isSubUnit || member.unitNames.length > 1) && roleText === roleTitle(role)
    ? member.unitNames.join(" · ")
    : null;

  return (
    <Card.Root borderWidth="1px" borderColor="border">
      <Card.Body p={4}>
        <HStack gap={4} align="flex-start">
          <Avatar.Root size="lg">
            <Avatar.Fallback name={member.fullName} />
            {member.profileImageUrl ? (
              <Avatar.Image src={member.profileImageUrl} alt={`${member.fullName} profile`} />
            ) : null}
          </Avatar.Root>
          <Stack gap={0.5} minW={0} flex="1">
            <HStack gap={2} minW={0} color="fg.muted">
              <RoleIcon role={role} />
              {/* Cut to one line, or a long sub-unit name wraps. The tooltip
                  has it whole. */}
              <Tooltip content={roleText} disabled={roleText === roleTitle(role)}>
                <Text truncate minW={0} fontSize="xs" fontWeight="semibold" textTransform="uppercase" letterSpacing="0.18em">
                  {roleText}
                </Text>
              </Tooltip>
            </HStack>
            {member.shortName ? (
              <Link to="/cemmies/$shortName" params={{ shortName: member.shortName }}>
                <Heading size="md" _hover={{ textDecoration: "underline" }}>{member.fullName}</Heading>
              </Link>
            ) : (
              <Heading size="md">{member.fullName}</Heading>
            )}
            {member.positionLabel ? <Text fontSize="sm">{member.positionLabel}</Text> : null}
            {subGroup ? <Text fontSize="sm" color="fg.muted">{subGroup}</Text> : null}
            {member.email ? (
              <TextLink>
                <a href={`mailto:${member.email}`}>
                  <Text as="span" fontSize="sm" truncate display="block">{member.email}</Text>
                </a>
              </TextLink>
            ) : null}
            {member.cohort !== null ? (
              <Text fontSize="sm" color="fg.muted">
                {member.programme ? `${PROGRAMME_LABELS[member.programme]} · ` : ""}Cohort {member.cohort}
              </Text>
            ) : null}
            {member.primaryGroup ? (
              <Text fontSize="sm" color="fg.muted">
                Primary group:{" "}
                <TextLink>
                  <Link to="/groups/$shortName" {...groupLink(member.primaryGroup)}>{member.primaryGroup.name}</Link>
                </TextLink>
              </Text>
            ) : null}
            {member.otherGroups.length > 0 ? (
              <Text fontSize="sm" color="fg.muted">
                Also in:{" "}
                {member.otherGroups.map((group, index) => (
                  <Fragment key={group.slug}>
                    {index > 0 ? ", " : null}
                    <TextLink>
                      <Link to="/groups/$shortName" {...groupLink(group)}>{group.name}</Link>
                    </TextLink>
                  </Fragment>
                ))}
              </Text>
            ) : null}
            {/* A non-counting basis: on site, outside the headcount. Shown
                only where it applies, so it reads as the exception. */}
            {member.countsAsCeMMie ? null : (
              <Text fontSize="sm" color="orange.fg">{member.basisLabel}</Text>
            )}
            {member.representativeRoles.length > 0 || member.status !== "cemmie" ? (
              <HStack gap={1.5} flexWrap="wrap" mt={1}>
                {member.representativeRoles.map((rep) => (
                  <Badge key={rep} colorPalette="purple" variant="subtle">{rep}</Badge>
                ))}
                {member.status === "cemmie" ? null : (
                  <Badge variant="subtle" colorPalette="gray">{member.statusLabel}</Badge>
                )}
              </HStack>
            ) : null}
          </Stack>
        </HStack>
      </Card.Body>
    </Card.Root>
  );
}

const memberColumnHelper = createColumnHelper<GroupMember>();

function MemberTable({ members, emptyLabel }: { members: GroupMember[]; emptyLabel: string }) {
  const data = useMemo(() => members, [members]);
  const columns = useMemo(
    () => [
      memberColumnHelper.accessor("fullName", {
        header: "Name",
        cell: ({ row }) => {
          const member = row.original;
          return (
            <HStack gap={3}>
              <Avatar.Root size="md">
                <Avatar.Fallback name={member.fullName} />
                {member.profileImageUrl ? (
                  <Avatar.Image src={member.profileImageUrl} alt={`${member.fullName} profile`} />
                ) : null}
              </Avatar.Root>
              <Box>
                {member.shortName ? (
                  <Link to="/cemmies/$shortName" params={{ shortName: member.shortName }}>
                    <Text fontWeight="semibold" _hover={{ textDecoration: "underline" }}>{member.fullName}</Text>
                  </Link>
                ) : (
                  <Text fontWeight="semibold">{member.fullName}</Text>
                )}
                <Text fontSize="xs" color="fg.muted">{member.shortName ?? "No user short name"}</Text>
              </Box>
            </HStack>
          );
        },
      }),
      memberColumnHelper.accessor("email", {
        header: "Email",
        cell: ({ row }) => (
          <HStack gap={2} fontSize="sm" color="fg.muted">
            <Icon as={LuMail} boxSize={4} />
            <Text>{row.original.email}</Text>
          </HStack>
        ),
      }),
      memberColumnHelper.display({
        id: "position",
        header: "Position",
        cell: ({ row }) => {
          const member = row.original;
          // Role and position in one column: the role is an icon in front of the
          // position, using the same symbols as the CeMMies list so a person
          // does not change shape depending on which page you arrived from.
          const role = resolveMemberRole({
            headRole: member.headRole,
            isStudent: member.isStudent,
          });
          return (
            <Stack gap={1} fontSize="sm" color="fg.muted">
              <HStack gap={2}>
                <RoleIcon role={role} />
                <Text>{member.positionLabel || "—"}</Text>
                {member.isPrimary ? null : (
                  // Their primary unit is elsewhere, so this is not where their
                  // activities are supervised.
                  <Badge variant="subtle" colorPalette="gray">secondary</Badge>
                )}
              </HStack>
              {/* A non-counting basis: on site, outside the headcount. Shown
                  only where it applies, so it reads as an exception. */}
              {member.countsAsCeMMie ? null : (
                <Text fontSize="xs" color="orange.fg">{member.basisLabel}</Text>
              )}
              {/* Which sub-unit they are in, when viewing a parent. More than
                  one where a person runs several -- the head of Proteomics and
                  Metabolomics is one member of the platform, listed once. */}
              {member.isSubUnit || member.unitNames.length > 1 ? (
                <Text fontSize="xs">{member.unitNames.join(" · ")}</Text>
              ) : null}
            </Stack>
          );
        },
      }),
      memberColumnHelper.display({
        id: "status",
        header: "Status",
        cell: ({ row }) => (
          <Badge
            colorPalette={row.original.status === "cemmie" ? "green" : "gray"}
            variant={row.original.status === "cemmie" ? "solid" : "subtle"}
          >
            {row.original.statusLabel}
          </Badge>
        ),
      }),
    ],
    [],
  );

  if (members.length === 0) {
    return (
      <Box borderWidth="1px" borderStyle="dashed" borderColor="border" px={5} py={6}>
        <Text fontSize="sm" color="fg.muted">{emptyLabel}</Text>
      </Box>
    );
  }

  return (
    <Box borderWidth="1px" borderColor="border">
      <DataTable
        data={data}
        columns={columns}
        getRowId={getMemberRowId}
        enableSorting
        stickyHeader
      />
    </Box>
  );
}

/**
 * One sub-group filter: a link, because the filter is part of the address.
 *
 * Replacing rather than pushing, so that Back leaves the page instead of
 * stepping back through every filter that was tried; and without the scroll
 * reset every navigation otherwise does, so the list does not jump to the top
 * of the page under the reader.
 */
function SubUnitFilterLink({
  groupSlug,
  subUnit,
  active,
  count,
  children,
}: {
  groupSlug: string;
  subUnit: string | undefined;
  active: boolean;
  count: number;
  children: ReactNode;
}) {
  const content = (
    <>
      {children}
      <Badge ml={2} size="sm" variant={active ? "solid" : "subtle"}>{count}</Badge>
    </>
  );
  // An empty sub-group stays visible, so the structure reads whole, but is not
  // a link: there is nothing to narrow to.
  if (count === 0 && !active) {
    return <Button size="sm" variant="outline" colorPalette="gray" disabled>{content}</Button>;
  }
  return (
    <Button asChild size="sm" variant={active ? "solid" : "outline"} colorPalette={active ? "blue" : "gray"}>
      <Link to="/groups/$shortName" params={{ shortName: groupSlug }} search={subUnit === undefined ? {} : { unit: subUnit }} replace resetScroll={false}>
        {content}
      </Link>
    </Button>
  );
}

export function GroupProfilePage() {
  const params = useParams({ from: "/_authed/groups/$shortName", structuralSharing: true });
  const search = useSearch({ from: "/_authed/groups/$shortName" });
  // Resolved by slug: short names are display codes and are not unique -- all
  // three Molecular Discovery Platform facilities carry "MDP".
  const profile = useQuery(api.persons.getGroupProfileBySlug, {
    slug: params.shortName,
  });
  const subUnitSlug = search.unit ?? null;
  const navigate = useNavigate({ from: "/groups/$shortName" });

  // Which sub-unit the member list is narrowed to, or null for everyone. The
  // address holds it, so a filtered list can be linked to and survives a
  // reload. The parent's own slug narrows to its direct members.
  const subUnitFilter = subUnitSlug === null || !profile
    ? null
    : subUnitSlug === profile.group.slug
      ? profile.group._id
      : (profile.group.children.find((child) => child.slug === subUnitSlug)?._id ?? null);

  // The unit's own leadership first, then its sub-units', then everybody
  // else alphabetically. On the Molecular Discovery Platform that puts the
  // head of the platform ahead of the three facility heads, who would
  // otherwise sort alphabetically among them and bury the person the page is
  // actually about. Heads before deputies within each level.
  const shownMembers = (subUnitFilter === null
    ? (profile?.currentMembers ?? [])
    : (profile?.currentMembers ?? []).filter((member) => member.unitId === subUnitFilter))
    .slice()
    .sort(
      (left, right) =>
        Number(left.headRole === null) - Number(right.headRole === null) ||
        Number(left.isSubUnit) - Number(right.isSubUnit) ||
        Number(right.headRole === "head") - Number(left.headRole === "head") ||
        left.fullName.localeCompare(right.fullName),
    );
  const shownHeads = shownMembers.filter((member) => member.headRole !== null);
  const shownOthers = shownMembers.filter((member) => member.headRole === null);

  return (
    <Box minH="full" bg="bg.subtle">
      <PageHeader
        title={profile?.group.name ?? params.shortName}
        description={profile?.group.description ?? ""}
        loading={profile === undefined}
        breadcrumbs={[{ label: "CeMMbase", to: "/" }, { label: "Groups", to: "/groups" }, { label: profile?.group.name ?? params.shortName }]}
        actions={profile ? (
          <HStack flexWrap="wrap" justify="flex-end" gap={2}>
            <GroupStatusBadge isClosed={profile.group.isClosed} />
            <TypeBadge label={profile.group.typeLabel ?? "Unknown"} />
            <Badge variant="outline">{profile.group.shortName}</Badge>
            {profile.group.url ? (
              <Button asChild variant="outline">
                <a href={profile.group.url} target="_blank" rel="noreferrer">
                  <LuExternalLink />
                  Group website
                </a>
              </Button>
            ) : null}
          </HStack>
        ) : null}
      />

      <Stack gap={8} px={{ base: 6, sm: 8, lg: 10 }} py={{ base: 6, lg: 8 }}>
        {profile === undefined ? (
          <CardGridSkeleton label="Loading the group" cards={8} columns={{ base: 1, md: 2, xl: 4 }} />
        ) : profile === null ? (
          <Card.Root borderWidth="1px" borderStyle="dashed" borderColor="border">
            <Card.Body gap={3} p={8} textAlign="center">
              <Heading size="xl">No group found</Heading>
              <Text fontSize="sm" color="fg.muted">No group with short name <Text as="span" fontWeight="semibold" color="fg">{params.shortName}</Text> exists in CeMMbase.</Text>
              <Box>
                <Button asChild colorPalette="blue">
                  <Link to="/groups">Back to groups</Link>
                </Button>
              </Box>
            </Card.Body>
          </Card.Root>
        ) : (
          <>
            <Stack as="section" gap={4}>
              <Box>
                <Heading size="xl">
                  {profile.stats.currentMembers} current member
                  {profile.stats.currentMembers === 1 ? "" : "s"}
                  {profile.stats.cemmies === profile.stats.currentMembers
                    ? ""
                    : ` · ${profile.stats.cemmies} count as CeMMies`}
                </Heading>
                {profile.group.children.length > 0 ? (
                  <Text mt={2} fontSize="sm" color="fg.muted">Including the members of its sub-groups.</Text>
                ) : null}
              </Box>

              {/* Sub-units filter this list rather than having pages of their
                  own, which would split Administration's people across eleven
                  views with no head and no reporting on any of them. */}
              {profile.group.children.length > 0 ? (
                <Wrap gap={2}>
                  <SubUnitFilterLink groupSlug={profile.group.slug} subUnit={undefined} active={subUnitFilter === null} count={profile.stats.currentMembers}>
                    All
                  </SubUnitFilterLink>
                  <SubUnitFilterLink groupSlug={profile.group.slug} subUnit={profile.group.slug} active={subUnitFilter === profile.group._id} count={profile.stats.ownMembers}>
                    {profile.group.name}
                  </SubUnitFilterLink>
                  {profile.group.children.map((child) => (
                    <SubUnitFilterLink key={child._id} groupSlug={profile.group.slug} subUnit={child.slug} active={subUnitFilter === child._id} count={child.memberCount}>
                      {child.name}
                    </SubUnitFilterLink>
                  ))}
                </Wrap>
              ) : null}

              {profile.group.children.length > 0 ? (
                <FilterCount
                  shown={shownMembers.length}
                  total={profile.currentMembers.length}
                  noun={["member", "members"]}
                  filtered={subUnitFilter !== null}
                  onClear={() => void navigate({ search: {}, ...searchUpdate })}
                />
              ) : null}
              {shownMembers.length === 0 ? (
                <Box borderWidth="1px" borderStyle="dashed" borderColor="border" px={5} py={6}>
                  <Text fontSize="sm" color="fg.muted">No current members are assigned to this group.</Text>
                </Box>
              ) : (
                // The heads on a row of their own, a line, then everybody else:
                // who runs the group, then who is in it.
                <Stack gap={5}>
                  {shownHeads.length > 0 ? (
                    <SimpleGrid gap={4} columns={{ base: 1, md: 2, xl: 3, "2xl": 4 }}>
                      {shownHeads.map((member) => (
                        <MemberCard key={member.affiliationId} member={member} />
                      ))}
                    </SimpleGrid>
                  ) : null}
                  {shownHeads.length > 0 && shownOthers.length > 0 ? <Separator /> : null}
                  {shownOthers.length > 0 ? (
                    <SimpleGrid gap={4} columns={{ base: 1, md: 2, xl: 3, "2xl": 4 }}>
                      {shownOthers.map((member) => (
                        <MemberCard key={member.affiliationId} member={member} />
                      ))}
                    </SimpleGrid>
                  ) : null}
                </Stack>
              )}
            </Stack>

            <Stack as="section" gap={4}>
              <Box>
                <Heading size="xl">
                  {profile.stats.pastMembers} past member
                  {profile.stats.pastMembers === 1 ? "" : "s"}
                </Heading>
                <Text mt={2} fontSize="sm" color="fg.muted">People who have left the group.</Text>
              </Box>
              <MemberTable members={profile.pastMembers} emptyLabel="No past members are assigned to this group." />
            </Stack>
          </>
        )}
      </Stack>
    </Box>
  );
}
